/**
 * ResidentsDirectoryPage.jsx
 * CCH Care — Phase 5b
 * Last Modified: 2026-04-21
 *
 * Facility-wide resident directory with:
 * - Search by name or room
 * - Filter by unit, alert type, ADL status
 * - Card view showing alerts, ADL progress, MAR summary, vitals
 * - Tap resident → ResidentDetail slide-in panel
 *
 * Changes (2026-04-21):
 * - ALERT_BADGE_COLORS removed. Now imports ALERT_BADGES from constants.js
 *   (18 alert tokens, shared with ResidentCard.jsx). Closes the 2026-04-20
 *   dark-mode TODO — tokens are now theme-responsive.
 *
 * Changes (2026-04-20) — Dark mode pass:
 * - ADL_STATUS_CONFIG.complete.bg: '#E8F8F0' → COLORS.accentLight.
 * - Vitals row hardcoded hex → tokens.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { COLORS, ALERT_BADGES } from '../../config/constants';
import client from '../../api/client';
import useShift from '../../hooks/useShift';
import ResidentDetail from '../dashboard/components/ResidentDetail';

const ADL_STATUS_CONFIG = {
  complete:    { label: 'Complete',    color: COLORS.accent,    bg: COLORS.accentLight   },
  in_progress: { label: 'In Progress', color: COLORS.warning,   bg: COLORS.warningLight  },
  not_started: { label: 'Not Started', color: COLORS.textMuted, bg: COLORS.bg            },
};

export default function ResidentsDirectoryPage() {
  const { shift } = useShift();

  const [residents, setResidents]               = useState([]);
  const [units, setUnits]                       = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState('');
  const [selectedResident, setSelectedResident] = useState(null);

  const [search,       setSearch]       = useState('');
  const [unitFilter,   setUnitFilter]   = useState('');
  const [alertFilter,  setAlertFilter]  = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters,  setShowFilters]  = useState(false);

  const careShiftId = shift?.care_shift_id ?? null;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (careShiftId) params.care_shift_id = careShiftId;
      const res = await client.get('/residents/directory.php', { params });
      if (res.data.success) {
        setResidents(res.data.residents || []);
        setUnits(res.data.units || []);
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load residents');
    } finally {
      setLoading(false);
    }
  }, [careShiftId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let list = [...residents];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.full_name.toLowerCase().includes(q) ||
        (r.room_number && r.room_number.toLowerCase().includes(q))
      );
    }
    if (unitFilter)   list = list.filter(r => String(r.unit_id) === String(unitFilter));
    if (alertFilter)  list = list.filter(r => r.alerts?.some(a => a.alert_type === alertFilter));
    if (statusFilter) list = list.filter(r => r.adl_status === statusFilter);
    return list;
  }, [residents, search, unitFilter, alertFilter, statusFilter]);

  const activeFilterCount = [unitFilter, alertFilter, statusFilter].filter(Boolean).length;

  const currentIndex = selectedResident
    ? filtered.findIndex(r => r.patient_id === selectedResident.patient_id)
    : -1;

  function clearFilters() {
    setUnitFilter(''); setAlertFilter(''); setStatusFilter(''); setSearch('');
  }

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, color: COLORS.textMuted }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>👥</div>
      <div style={{ fontSize: 14 }}>Loading residents…</div>
    </div>
  );

  if (error) return (
    <div style={{ padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 32, marginBottom: 10 }}>⚠️</div>
      <div style={{ color: COLORS.danger, fontSize: 14, marginBottom: 16 }}>{error}</div>
      <button onClick={load} style={{
        padding: '8px 20px', borderRadius: 8, background: COLORS.primary,
        color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
      }}>Retry</button>
    </div>
  );

  return (
    <div style={{ paddingBottom: 80 }}>

      <div style={{
        padding: '14px 16px 10px', background: COLORS.surface,
        borderBottom: `1px solid ${COLORS.border}`,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: COLORS.text }}>All Residents</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 1 }}>
              {filtered.length} of {residents.length} residents
            </div>
          </div>
          <button
            onClick={() => setShowFilters(f => !f)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
              background: activeFilterCount > 0 ? COLORS.primary : 'none',
              color: activeFilterCount > 0 ? '#fff' : COLORS.textMuted,
              border: `1px solid ${activeFilterCount > 0 ? COLORS.primary : COLORS.border}`,
              cursor: 'pointer',
            }}
          >
            ⚙️ Filters {activeFilterCount > 0 && `· ${activeFilterCount}`}
          </button>
        </div>

        <input
          type="text" value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or room…"
          style={{
            width: '100%', padding: '8px 12px', borderRadius: 8,
            border: `1px solid ${COLORS.border}`, fontSize: 13,
            color: COLORS.text, background: COLORS.bg,
            boxSizing: 'border-box', fontFamily: 'inherit',
          }}
        />

        {showFilters && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {units.length > 1 && (
              <select value={unitFilter} onChange={e => setUnitFilter(e.target.value)} style={selectStyle}>
                <option value="">All Units</option>
                {units.map(u => (
                  <option key={u.unit_id} value={u.unit_id}>{u.name}</option>
                ))}
              </select>
            )}
            <select value={alertFilter} onChange={e => setAlertFilter(e.target.value)} style={selectStyle}>
              <option value="">All Alert Types</option>
              <option value="fall_risk">Fall Risk</option>
              <option value="dnr">DNR</option>
              <option value="elopement_risk">Elopement Risk</option>
              <option value="diet_restriction">Diet Restriction</option>
              <option value="diabetic">Diabetic</option>
              <option value="isolation">Isolation</option>
              <option value="aspiration_risk">Aspiration Risk</option>
              <option value="allergy">Allergy</option>
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selectStyle}>
              <option value="">All ADL Statuses</option>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="complete">Complete</option>
            </select>
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} style={{
                padding: '7px 0', borderRadius: 8, fontSize: 12, fontWeight: 600,
                background: 'none', color: COLORS.danger,
                border: `1px solid ${COLORS.danger}`, cursor: 'pointer',
              }}>
                ✕ Clear All Filters
              </button>
            )}
          </div>
        )}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: COLORS.textMuted }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text, marginBottom: 6 }}>No residents found</div>
          <div style={{ fontSize: 13 }}>Try adjusting your search or filters.</div>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} style={{
              marginTop: 16, padding: '8px 20px', borderRadius: 8,
              background: COLORS.primary, color: '#fff', border: 'none',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>
              Clear Filters
            </button>
          )}
        </div>
      )}

      {filtered.map(r => (
        <ResidentDirectoryCard
          key={r.patient_id}
          resident={r}
          onTap={() => setSelectedResident(r)}
        />
      ))}

      {selectedResident && (
        <ResidentDetail
          patientId={selectedResident.patient_id}
          careShiftId={careShiftId}
          onClose={() => setSelectedResident(null)}
          onPrev={currentIndex > 0 ? () => setSelectedResident(filtered[currentIndex - 1]) : null}
          onNext={currentIndex < filtered.length - 1 ? () => setSelectedResident(filtered[currentIndex + 1]) : null}
          currentIndex={currentIndex}
          totalCount={filtered.length}
        />
      )}
    </div>
  );
}

function ResidentDirectoryCard({ resident: r, onTap }) {
  const adlCfg      = ADL_STATUS_CONFIG[r.adl_status] || ADL_STATUS_CONFIG.not_started;
  const pct         = r.adl_percent || 0;
  const barColor    = pct === 100 ? COLORS.accent : pct >= 50 ? COLORS.primary : COLORS.warning;
  const hasVitals   = !!r.latest_vitals;
  const hasAbnormal = hasVitals && r.latest_vitals.has_abnormal;
  const hasMeds     = r.mar_total > 0;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onTap}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onTap()}
      style={{
        background: COLORS.surface,
        borderBottom: `1px solid ${COLORS.border}`,
        padding: '12px 16px',
        cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
          background: COLORS.primaryLight, color: COLORS.primary,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 700,
        }}>
          {r.initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{r.full_name}</span>
            {r.is_new_admit && (
              <span style={{
                fontSize: 9, fontWeight: 700, color: COLORS.primary,
                background: COLORS.primaryLight, padding: '1px 5px',
                borderRadius: 4, textTransform: 'uppercase',
              }}>New</span>
            )}
          </div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 1 }}>
            Rm {r.room_number}
            {r.bed_label  ? ` · ${r.bed_label}`  : ''}
            {r.unit_name  ? ` · ${r.unit_name}`  : ''}
            {r.age        ? ` · ${r.age}y`        : ''}
            {r.gender     ? ` · ${r.gender}`      : ''}
          </div>
        </div>
        <span style={{
          fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0,
          color: adlCfg.color, background: adlCfg.bg,
          padding: '3px 8px', borderRadius: 20,
        }}>
          {adlCfg.label}
        </span>
      </div>

      {r.alerts?.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {r.alerts.map((a, i) => {
            const cfg = ALERT_BADGES[a.alert_type] || ALERT_BADGES.custom;
            return (
              <span key={i} style={{
                fontSize: 10, fontWeight: 700,
                background: cfg.bg, color: cfg.text,
                padding: '2px 7px', borderRadius: 4,
              }}>
                {a.alert_label}
              </span>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, height: 4, borderRadius: 2, background: COLORS.bg, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: 2, transition: 'width 0.3s ease' }} />
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, color: barColor, flexShrink: 0 }}>
          {r.adl_done}/{r.adl_total} ADL
        </span>
      </div>

      {(hasMeds || hasVitals) && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {hasMeds && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 4,
              background: r.mar_pending > 0 ? COLORS.warningLight : COLORS.bg,
              border: `1px solid ${r.mar_pending > 0 ? COLORS.warning : COLORS.border}`,
              borderRadius: 6, padding: '3px 9px', fontSize: 11,
            }}>
              <span>💊</span>
              <span style={{ color: COLORS.text, fontWeight: 600 }}>{r.mar_given}/{r.mar_total} given</span>
              {r.mar_pending > 0 && <span style={{ color: COLORS.warning, fontWeight: 700 }}>· {r.mar_pending} pending</span>}
              {r.mar_controlled > 0 && <span style={{ color: COLORS.danger, fontWeight: 700 }}>· ⚠ {r.mar_controlled} CS</span>}
            </div>
          )}
          {hasVitals && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 4,
              background: hasAbnormal ? COLORS.warningLight : COLORS.bg,
              border: `1px solid ${hasAbnormal ? COLORS.warning : COLORS.border}`,
              borderRadius: 6, padding: '3px 9px', fontSize: 11,
            }}>
              <span>🩺</span>
              <span style={{ color: hasAbnormal ? COLORS.warning : COLORS.text, fontWeight: 600 }}>
                {r.latest_vitals.systolic && r.latest_vitals.diastolic
                  ? `BP ${r.latest_vitals.systolic}/${r.latest_vitals.diastolic}`
                  : 'Vitals on file'}
              </span>
              {hasAbnormal && <span style={{ color: COLORS.warning, fontWeight: 700 }}>⚠</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const selectStyle = {
  width: '100%', padding: '8px 10px', borderRadius: 8,
  border: `1px solid ${COLORS.border}`, fontSize: 13,
  color: COLORS.text, background: COLORS.surface,
  fontFamily: 'inherit', boxSizing: 'border-box',
};